import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, LogIn, Users, UserCog, GraduationCap, KeyRound, Users2, Calendar, ChevronLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { apiErrorMsg } from "@/lib/api";

const ROLES = [
  { key: "admin", label: "Admin", icon: UserCog, desc: "Login dengan username & sandi" },
  { key: "pengasuh", label: "Pengasuh", icon: Users, desc: "Pilih nama & masukkan sandi" },
  { key: "siswa", label: "Serdik", icon: GraduationCap, desc: "Pilih Kelompok → Jadwal → Nama" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [role, setRole] = useState("admin");
  const [pengasuhList, setPengasuhList] = useState([]);
  // === Serdik step-by-step selection ===
  const [siswaStep, setSiswaStep] = useState(1); // 1=kelompok, 2=periode, 3=nama
  const [kelompokList, setKelompokList] = useState([]);
  const [selectedKelompok, setSelectedKelompok] = useState(null);
  const [periodeList, setPeriodeList] = useState([]);
  const [selectedPeriode, setSelectedPeriode] = useState(null);
  const [siswaList, setSiswaList] = useState([]);
  const [siswaLoading, setSiswaLoading] = useState(false);

  const [form, setForm] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(`/${user.role === "admin" ? "admin" : user.role}`, { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get("/auth/pengasuh-list").then((r) => setPengasuhList(r.data)).catch(() => {});
  }, []);

  // Reset form + reset serdik wizard saat ganti role
  useEffect(() => {
    setForm({ identifier: "", password: "" });
    setSiswaStep(1);
    setSelectedKelompok(null);
    setSelectedPeriode(null);
    setPeriodeList([]);
    setSiswaList([]);
    if (role === "siswa") {
      // Ambil kelompok public dengan periode open
      api.get("/auth/kelompok-list")
        .then((r) => setKelompokList(r.data))
        .catch(() => setKelompokList([]));
    }
  }, [role]);

  // Ketika kelompok dipilih → load periode
  const chooseKelompok = async (k) => {
    setSelectedKelompok(k);
    setSelectedPeriode(null);
    setSiswaList([]);
    try {
      const r = await api.get("/auth/periode-list", { params: { kelompok_id: k.id } });
      setPeriodeList(r.data);
      setSiswaStep(2);
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  // Ketika periode dipilih → load daftar siswa periode itu
  const choosePeriode = async (p) => {
    setSelectedPeriode(p);
    setSiswaLoading(true);
    try {
      const r = await api.get("/auth/siswa-list", { params: { periode_id: p.id } });
      setSiswaList(r.data);
      setSiswaStep(3);
    } catch (e) {
      toast.error(apiErrorMsg(e));
    } finally {
      setSiswaLoading(false);
    }
  };

  const backStep = () => {
    if (siswaStep === 3) {
      setSiswaStep(2);
      setForm({ identifier: "", password: "" });
      setSiswaList([]);
      setSelectedPeriode(null);
    } else if (siswaStep === 2) {
      setSiswaStep(1);
      setSelectedKelompok(null);
      setPeriodeList([]);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.identifier) {
      toast.error("Pilih/masukkan identitas terlebih dahulu");
      return;
    }
    if (role !== "siswa" && !form.password) {
      toast.error("Masukkan sandi");
      return;
    }
    setLoading(true);
    try {
      const u = await login(role, form.identifier, role === "siswa" ? "" : form.password);
      toast.success(`Selamat datang, ${u.username || u.nama}`);
      navigate(`/${u.role === "admin" ? "admin" : u.role}`, { replace: true });
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const periodeLabel = (p) => {
    const jenis = p.jenis === "mingguan" ? "Minggu" : "Bulan";
    const nama = p.nama_periode ? ` — ${p.nama_periode}` : "";
    return `${jenis} ke-${p.nomor} · ${p.bulan} ${p.tahun}${nama}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Hero */}
      <div className="login-hero hidden md:flex flex-col justify-between p-12 text-white relative">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-sm bg-brass-500 flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <div className="font-heading font-bold text-sm tracking-[0.2em] uppercase">Sistem Penilaian</div>
              <div className="text-xs text-white/60 tracking-wide">Serdik Terpadu</div>
            </div>
          </div>
          <h1 className="font-heading text-5xl font-bold leading-tight max-w-md">
            Integrasi Sosiometri<br />
            <span className="text-brass-400">& Catatan Siku</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-md leading-relaxed">
            Platform digital untuk penilaian sikap dan perilaku perorangan Serdik
            sesuai standar pendidikan TNI AD.
          </p>
        </div>
        <div className="space-y-2 text-xs text-white/50 font-mono uppercase tracking-wider">
          <div>Pusat Pendidikan Penerbangan</div>
          <div>Satuan Pendidikan Perwira</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col justify-center p-6 sm:p-8 md:p-16 bg-white">
        <div className="md:hidden mb-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-navy-900 flex items-center justify-center">
            <Shield className="w-5 h-5 text-brass-400" />
          </div>
          <div>
            <div className="font-heading font-bold text-sm tracking-widest uppercase">Sistem Penilaian</div>
          </div>
        </div>

        <h2 className="font-heading text-3xl font-bold text-navy-900 mb-2">Masuk ke Sistem</h2>
        <p className="text-slate-500 text-sm mb-8">Pilih peran Anda untuk melanjutkan</p>

        <div className="grid grid-cols-3 gap-2 mb-8" data-testid="role-tabs">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const active = role === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => setRole(r.key)}
                data-testid={`role-tab-${r.key}`}
                className={`p-3 rounded-sm border text-left transition-all ${
                  active
                    ? "border-brass-500 bg-brass-50 ring-1 ring-brass-500"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <Icon className={`w-5 h-5 mb-2 ${active ? "text-brass-600" : "text-slate-500"}`} />
                <div className={`font-semibold text-sm ${active ? "text-navy-900" : "text-slate-700"}`}>{r.label}</div>
              </button>
            );
          })}
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
          {role === "admin" && (
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Username</label>
              <input
                type="text"
                value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                placeholder="Satdikpa"
                data-testid="login-username"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brass-500 focus:border-brass-500"
              />
            </div>
          )}

          {role === "pengasuh" && (
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Nama Pengasuh</label>
              <select
                value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                data-testid="login-pengasuh-select"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brass-500 focus:border-brass-500 bg-white"
              >
                <option value="">-- Pilih Pengasuh --</option>
                {pengasuhList.map((p) => (
                  <option key={p.id} value={p.id}>{p.nama}</option>
                ))}
              </select>
              {pengasuhList.length === 0 && <p className="text-xs text-amber-700 mt-1">Belum ada pengasuh terdaftar. Hubungi admin.</p>}
            </div>
          )}

          {role === "siswa" && (
            <div data-testid="siswa-login-wizard">
              {/* Breadcrumb steps */}
              <div className="flex items-center gap-2 text-xs mb-4">
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border ${siswaStep >= 1 ? "border-brass-500 bg-brass-50 text-brass-800" : "border-slate-200 text-slate-400"}`}>
                  <Users2 className="w-3 h-3" /> 1. Kelompok
                </div>
                <div className="text-slate-300">›</div>
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border ${siswaStep >= 2 ? "border-brass-500 bg-brass-50 text-brass-800" : "border-slate-200 text-slate-400"}`}>
                  <Calendar className="w-3 h-3" /> 2. Jadwal
                </div>
                <div className="text-slate-300">›</div>
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border ${siswaStep >= 3 ? "border-brass-500 bg-brass-50 text-brass-800" : "border-slate-200 text-slate-400"}`}>
                  <GraduationCap className="w-3 h-3" /> 3. Nama
                </div>
              </div>

              {/* Step 1: pilih kelompok */}
              {siswaStep === 1 && (
                <div>
                  <label className="block text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Pilih Kelompok</label>
                  {kelompokList.length === 0 ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800">
                      Belum ada kelompok dengan jadwal pengisian terbuka. Hubungi admin.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[280px] overflow-auto" data-testid="siswa-login-kelompok-list">
                      {kelompokList.map((k) => (
                        <button
                          key={k.id}
                          type="button"
                          onClick={() => chooseKelompok(k)}
                          data-testid={`siswa-login-kelompok-${k.id}`}
                          className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-sm hover:border-brass-500 hover:bg-brass-50 text-left transition-all group"
                        >
                          <div>
                            <div className="font-semibold text-navy-900">{k.nama}</div>
                            {k.pendidikan && <div className="text-xs text-slate-500 mt-0.5">{k.pendidikan}</div>}
                          </div>
                          <div className="text-slate-300 group-hover:text-brass-600 text-lg">›</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: pilih periode */}
              {siswaStep === 2 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Pilih Jadwal Pengisian</label>
                    <button type="button" onClick={backStep} data-testid="siswa-login-back-1" className="text-xs text-slate-500 hover:text-navy-900 flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Ganti Kelompok</button>
                  </div>
                  <div className="mb-2 p-2 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-600">
                    Kelompok: <span className="font-semibold text-navy-900">{selectedKelompok?.nama}</span>
                  </div>
                  {periodeList.length === 0 ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800">
                      Belum ada jadwal pengisian yang terbuka untuk kelompok ini.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[280px] overflow-auto" data-testid="siswa-login-periode-list">
                      {periodeList.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => choosePeriode(p)}
                          data-testid={`siswa-login-periode-${p.id}`}
                          className="w-full flex items-center justify-between p-3 border border-slate-200 rounded-sm hover:border-brass-500 hover:bg-brass-50 text-left transition-all group"
                        >
                          <div>
                            <div className="font-semibold text-navy-900 text-sm">{periodeLabel(p)}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{(p.siswa_ids || []).length} Serdik terdaftar</div>
                          </div>
                          <div className="text-slate-300 group-hover:text-brass-600 text-lg">›</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: pilih nama siswa */}
              {siswaStep === 3 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Pilih Nama Serdik</label>
                    <button type="button" onClick={backStep} data-testid="siswa-login-back-2" className="text-xs text-slate-500 hover:text-navy-900 flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Ganti Jadwal</button>
                  </div>
                  <div className="mb-2 p-2 bg-slate-50 border border-slate-200 rounded-sm text-xs text-slate-600 space-y-0.5">
                    <div>Kelompok: <span className="font-semibold text-navy-900">{selectedKelompok?.nama}</span></div>
                    <div>Jadwal: <span className="font-semibold text-navy-900">{selectedPeriode ? periodeLabel(selectedPeriode) : ""}</span></div>
                  </div>
                  {siswaLoading ? (
                    <div className="p-4 text-xs text-slate-500">Memuat daftar Serdik...</div>
                  ) : siswaList.length === 0 ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800">Tidak ada Serdik pada jadwal ini.</div>
                  ) : (
                    <select
                      value={form.identifier}
                      onChange={(e) => setForm({ ...form, identifier: e.target.value })}
                      data-testid="login-siswa-select"
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brass-500 focus:border-brass-500 bg-white"
                    >
                      <option value="">-- Pilih Serdik --</option>
                      {siswaList.map((s) => (
                        <option key={s.id} value={s.id}>{s.nama}{s.nosis ? ` — ${s.nosis}` : ""}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          )}

          {role !== "siswa" && (
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Sandi</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="login-password"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brass-500 focus:border-brass-500"
                />
              </div>
            </div>
          )}

          {/* Tombol Masuk hanya muncul saat: (a) role non-siswa, atau (b) siswa sudah di step 3 dengan identifier terpilih */}
          {(role !== "siswa" || (siswaStep === 3 && form.identifier)) && (
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-navy-900 hover:bg-navy-800 disabled:opacity-50 text-white font-semibold py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors mt-2"
            >
              <LogIn className="w-4 h-4" />
              {loading ? "Memuat..." : "Masuk"}
            </button>
          )}
        </form>

        <div className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-500">
          <div className="font-semibold text-slate-600 mb-1">Petunjuk:</div>
          <ul className="space-y-0.5 leading-relaxed">
            <li>• <strong>Admin</strong>: gunakan username dan sandi yang diberikan</li>
            <li>• <strong>Pengasuh</strong>: sandi awal <code className="font-mono bg-slate-100 px-1">pengasuh123</code></li>
            <li>• <strong>Serdik</strong>: pilih Kelompok → Jadwal Pengisian → Nama Anda</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
